import { createContext, useContext, useEffect, useState } from "react";

const AccessibilityContext = createContext();

const STORAGE_KEY = "a11y";

// One source of truth for defaults — also used by reset().
export const DEFAULT_A11Y = {
    textSize: 0, // 0 = normal, 1 = large, 2 = larger, 3 = largest
    highContrast: false,
    reduceMotion: false,
    colorBlind: "none", // none | protanopia | deuteranopia | tritanopia
    noTransparency: false,
    boldText: false,
};

// Font scale applied through the --a11y-font-scale CSS variable.
const FONT_SCALES = [1, 1.15, 1.3, 1.5];

const COLORBLIND_TYPES = ["protanopia", "deuteranopia", "tritanopia"];

function loadSettings() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return { ...DEFAULT_A11Y };
        const parsed = JSON.parse(raw);
        return { ...DEFAULT_A11Y, ...parsed };
    } catch {
        return { ...DEFAULT_A11Y };
    }
}

// Reflect the settings object onto <html>/<body> so global CSS can react.
function applySettings(settings) {
    const root = document.documentElement;
    const body = document.body;

    const scaleIndex = Math.min(Math.max(settings.textSize | 0, 0), FONT_SCALES.length - 1);
    root.style.setProperty("--a11y-font-scale", String(FONT_SCALES[scaleIndex]));

    body.classList.toggle("a11y-contrast", !!settings.highContrast);
    body.classList.toggle("a11y-reduce-motion", !!settings.reduceMotion);
    body.classList.toggle("a11y-no-transparency", !!settings.noTransparency);
    body.classList.toggle("a11y-bold", !!settings.boldText);

    COLORBLIND_TYPES.forEach((type) => {
        body.classList.toggle(`a11y-cb-${type}`, settings.colorBlind === type);
    });
}

export function AccessibilityProvider({ children }) {
    const [settings, setSettings] = useState(loadSettings);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
        applySettings(settings);
    }, [settings]);

    const setSetting = (key, value) => {
        setSettings((prev) => ({ ...prev, [key]: value }));
    };

    const reset = () => setSettings({ ...DEFAULT_A11Y });

    return (
        <AccessibilityContext.Provider value={{ settings, setSetting, reset }}>
            {children}
        </AccessibilityContext.Provider>
    );
}

export function useAccessibility() {
    return useContext(AccessibilityContext);
}
